import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { chartAPI, CandleData, HomeCurrencyData } from '@/api/endpoints';

export type Timeframe = '1m' | '15m' | '1h' | '4h' | '1d';

export const TIMEFRAMES: Timeframe[] = ['1m', '15m', '1h', '4h', '1d'];

interface UseChartReturn {
  chartData: CandleData[];
  homeCurrencyData: HomeCurrencyData | null;
  isLoading: boolean;
  error: string | null;
  selectedTimeframe: Timeframe;
  setTimeframe: (timeframe: Timeframe) => void;
  refreshChart: () => Promise<void>;
}

/**
 * Calculate start time based on selected interval
 * Each timeframe shows approximately 30 candles
 */
const calculateStartTime = (interval: Timeframe, now: number): number => {
  switch (interval) {
    case '1m':
      return now - 30 * 60 * 1000; // 30 minutes
    case '15m':
      return now - 7.5 * 60 * 60 * 1000; // 7.5 hours
    case '1h':
      return now - 30 * 60 * 60 * 1000; // 30 hours
    case '4h':
      return now - 5 * 24 * 60 * 60 * 1000; // 5 days
    case '1d':
      return now - 30 * 24 * 60 * 60 * 1000; // 30 days
    default:
      return now - 30 * 60 * 1000;
  }
};

export const useChart = (): UseChartReturn => {
  const [chartData, setChartData] = useState<CandleData[]>([]);
  const [homeCurrencyData, setHomeCurrencyData] = useState<HomeCurrencyData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedTimeframe, setSelectedTimeframe] = useState<Timeframe>('1m');

  // Use ref to keep consistent end time across fetches
  const nowTimestamp = useRef(Date.now());

  const startTime = useMemo(
    () => calculateStartTime(selectedTimeframe, nowTimestamp.current),
    [selectedTimeframe]
  );

  const fetchChartData = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const data = await chartAPI.getMainChartData({
        currency: 'BTCUSDT',
        interval: selectedTimeframe,
        startTime,
        endTime: nowTimestamp.current,
      });
      setChartData(data);
    } catch (err: unknown) {
      const errorMessage =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Failed to fetch chart data';
      setError(errorMessage);
      console.error('Chart fetch error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [selectedTimeframe, startTime]);

  const fetchHomeCurrencyData = useCallback(async () => {
    try {
      const data = await chartAPI.getHomeCurrencyData();
      setHomeCurrencyData(data);
    } catch (err) {
      console.error('Home currency data fetch error:', err);
    }
  }, []);

  const refreshChart = useCallback(async () => {
    nowTimestamp.current = Date.now();
    await Promise.all([fetchChartData(), fetchHomeCurrencyData()]);
  }, [fetchChartData, fetchHomeCurrencyData]);

  const setTimeframe = useCallback((timeframe: Timeframe) => {
    setSelectedTimeframe(timeframe);
    nowTimestamp.current = Date.now();
  }, []);

  // Fetch data on mount and when timeframe changes
  useEffect(() => {
    fetchChartData();
    fetchHomeCurrencyData();
  }, [fetchChartData, fetchHomeCurrencyData]);

  return {
    chartData,
    homeCurrencyData,
    isLoading,
    error,
    selectedTimeframe,
    setTimeframe,
    refreshChart,
  };
};
